// One playback slot (main player = tile 0, multiview tiles 0..3): lease lifecycle, engine, heartbeat, recovery,
// first-frame watchdog, progress, audio/subtitle language choice. Framework-free; React subscribes via
// subscribe/getSnapshot. PLAY03-05, VOD03. ADR-0006: direct sessions renew their provider token URL on reconnect;
// external sessions (HTTP-only provider on an HTTPS app) never attach an engine and track no progress.
import { errorMessage, type ErrorCode, type PlaybackSession, type Preferences, type StateOperation } from '@tuned/contracts';
import { api, ApiRequestError } from '../../app/api.ts';
import {
  attachEngine, probeCapabilities, unsupportedFileReason, type Decision, type EngineFault, type EngineHandle, type EngineName, type Track, type Transport,
} from './engine.ts';
import { initialState, reduce, MAX_RECONNECTS, type MachineEvent, type PlayRequest, type PlayerError, type PlayerStatus } from './machine.ts';
import { trackProgress } from './progress.ts';

export const WATCHDOG_MS = 20_000;     // no first frame / endless buffering → never an endless spinner (PLAY01)
export const STABLE_MS = 30_000;       // this long playing refills the reconnect budget
export const PARK_MS = 30_000;         // paused mpegts.js live stops downloading (PLAY04 64 MiB budget)
export const RELEASE_TIMEOUT_MS = 3_000;
export const RECONNECT_BACKOFF_MS = 2_000;  // × attempt (2 s, 4 s)
export const LANGUAGE_SAVE_MS = 1_000;      // track changes are saved as the new default after this quiet period

export type Diagnostics = {
  engine: EngineName | null; delivery: string | null; mediaPath: string | null; lastHttpStatus: number | null;
  /** 'downgraded' = provider redirected https→http (relay header x-tuned-transport). */
  transport: Transport;
  /** Progressive only: native / convert / unsupported (ADR-0004). */
  decision: Decision | null;
  /** Last engine fault (kind: reason) this session, kept after recovery so a stuck "Reconnecting" can be explained. */
  lastFault?: string | null;
};
export type ControllerSnapshot = {
  status: PlayerStatus;
  error: PlayerError | null;
  request: PlayRequest | null;
  session: PlaybackSession | null;
  engine: EngineName | null;
  reconnects: number;
  maxReconnects: number;
  /** Live stream parked while paused: resuming jumps to live. */
  parked: boolean;
  /** Bumped when engine tracks/levels change. */
  tracks: number;
  diagnostics: Diagnostics;
};
export type LiveInfo = { window: { start: number; end: number } | null; delay: number; expired: boolean };
export type LanguagePrefs = Pick<Preferences, 'audioLanguage' | 'subtitleLanguage'>;
export type ControllerOptions = {
  video: HTMLVideoElement;
  tile: number;
  onProgress?: (op: StateOperation) => void;
  /** Saved audio/subtitle languages; null until user state has loaded. */
  preferences?: () => LanguagePrefs | null;
  /** A track the viewer picked becomes the default for other titles (debounced). */
  onPreferences?: (p: Partial<LanguagePrefs>) => void;
};

/** VOD03: 'en' ~ 'eng' ~ 'en-US' — tags compare by canonical primary language (ISO 639-1/-2, BCP 47). */
export function langKey(tag: string | null | undefined): string | null {
  if (!tag) return null;
  try { return new Intl.Locale(tag).language; } catch { return tag.toLowerCase().split(/[-_]/)[0] || null; }
}
/** Id of the first track in `lang`; undefined keeps the stream's default. */
export function trackFor(tracks: Track[], lang: string | null | undefined): number | undefined {
  const k = langKey(lang);
  return k ? tracks.find((t) => langKey(t.lang) === k)?.id : undefined;
}
// Last explicit choice per asset (itemId / channelId), this page session only (VOD03 per-asset fallback).
const chosen = new Map<string, { audio?: string | null; subtitle?: string | null }>();
export const clearChosenLanguagesForTests = () => chosen.clear();
export type Controller = ReturnType<typeof createController>;

const sleep = (ms: number) => new Promise<void>((r) => setTimeout(r, ms));
const noop = () => {};
const sessionPath = (id: string) => `/api/playback/${encodeURIComponent(id)}`;

export function toPlayerError(err: unknown): PlayerError {
  if (err instanceof ApiRequestError) {
    return { code: err.code, message: err.message || errorMessage[err.code], retryable: err.retryable, requestId: err.requestId || undefined, httpStatus: err.status, details: err.details };
  }
  return { code: 'NETWORK', message: 'Tuned could not reach its server. Check your connection, then retry.', retryable: true };
}
function faultError(f: EngineFault): PlayerError {
  if (f.code) {
    const message = errorMessage[f.code as ErrorCode] ?? "Tuned couldn't convert this title in your browser. Try another title, or Safari.";
    return { code: f.code, message, retryable: false, httpStatus: f.httpStatus, details: { reason: f.reason } };
  }
  return f.kind === 'network'
    ? { code: 'STREAM_INTERRUPTED', message: `The stream stopped and ${MAX_RECONNECTS} reconnects did not bring it back. Retry, or pick another channel.`, retryable: true, httpStatus: f.httpStatus, details: { reason: f.reason } }
    : { code: 'PLAYBACK_ERROR', message: 'The browser could not keep decoding this stream. Retry, or pick another channel.', retryable: true, details: { reason: f.reason } };
}

export function createController(opts: ControllerOptions) {
  const { video, tile } = opts;
  let m = initialState;
  let request: PlayRequest | null = null;
  let session: PlaybackSession | null = null;
  let handle: EngineHandle | null = null;
  const noDiagnostics: Diagnostics = { engine: null, delivery: null, mediaPath: null, lastHttpStatus: null, transport: null, decision: null };
  let diagnostics = noDiagnostics;
  let gen = 0;              // bumps on play/stop: stale async work bails
  let attachSeq = 0;        // bumps per engine instance: stale faults are ignored
  let chain: Promise<void> = Promise.resolve();
  let releasing: Promise<unknown> = Promise.resolve();
  let hbTimer: ReturnType<typeof setInterval> | undefined;
  let watchdog: ReturnType<typeof setTimeout> | undefined;
  let stableTimer: ReturnType<typeof setTimeout> | undefined;
  let parkTimer: ReturnType<typeof setTimeout> | undefined;
  let progress: ReturnType<typeof trackProgress> | null = null;
  let resumeAt: number | null = null;
  let baseline: number | null = null;
  let parked = false;
  let tracks = 0;
  let playT0 = 0;
  let firstFrame: number | null = null;
  let applied = { audio: false, subtitle: false };   // per engine instance: languages are picked once
  let prefPatch: Partial<LanguagePrefs> = {};
  let prefTimer: ReturnType<typeof setTimeout> | undefined;
  const listeners = new Set<() => void>();
  let snap = build();

  function build(): ControllerSnapshot {
    return { status: m.status, error: m.error, request, session, engine: handle?.name ?? diagnostics.engine, reconnects: m.reconnects, maxReconnects: MAX_RECONNECTS, parked, tracks, diagnostics };
  }
  function emit() {
    if (handle?.transport() && !diagnostics.transport) diagnostics = { ...diagnostics, transport: handle.transport() };
    snap = build();
    listeners.forEach((l) => l());
  }

  function dispatch(e: MachineEvent) {
    const prev = m;
    m = reduce(m, e);
    if (m === prev) return;
    const st = m.status;
    if (st !== prev.status) {
      if (st === 'buffering') arm();
      else if (st !== 'connecting' && st !== 'recovering') clearTimeout(watchdog);
      clearTimeout(stableTimer);
      if (st === 'playing') stableTimer = setTimeout(() => dispatch({ type: 'stable' }), STABLE_MS);
      clearTimeout(parkTimer);
      if (st === 'paused' && handle?.name === 'mpegts' && session?.capabilities.live) parkTimer = setTimeout(park, PARK_MS);
      if (st === 'failed') closeCurrent();   // stop network + release the lease; Retry opens a new one
    }
    if (st === 'recovering' && m.reconnects > prev.reconnects) void recover('network', m.reconnects);
    if (st === 'recovering' && m.mediaRecoveries > prev.mediaRecoveries) void recover('media', m.mediaRecoveries);
    emit();
  }

  function arm() {
    clearTimeout(watchdog);
    watchdog = setTimeout(() => {
      if (m.status === 'connecting' && handle?.name === 'progressive' && session && video.readyState >= 1 && video.videoWidth === 0) {
        // Metadata loaded but no picture: the container opened, its codec did not decode (no retries, PLAY01).
        dispatch({ type: 'fail', error: { code: 'UNSUPPORTED_MEDIA', message: errorMessage.UNSUPPORTED_MEDIA, retryable: false, details: { reason: unsupportedFileReason(session.mimeType, 3) } } });
      } else if (m.status === 'connecting') {
        dispatch({ type: 'fail', error: { code: 'START_TIMEOUT', message: 'The channel did not start within 20 seconds. The provider may be slow or the channel offline. Retry, or pick another channel.', retryable: true } });
      } else if (m.status === 'buffering' || m.status === 'recovering') {
        dispatch({ type: 'fault', kind: 'network', error: faultError({ kind: 'network', reason: 'No video for 20 seconds' }) });
      }
    }, WATCHDOG_MS);
  }

  // ---- media element → machine ----
  const markFrame = () => { if (firstFrame === null && session && video.readyState >= 2) firstFrame = Math.round(performance.now() - playT0); };
  const media: Record<string, () => void> = {
    play: () => dispatch({ type: 'media', event: 'play' }),
    playing: () => {
      markFrame();
      if (baseline === null && session?.capabilities.live) { const w = handle?.seekWindow(); baseline = w ? Math.max(0, w.end - video.currentTime) : 0; }
      dispatch({ type: 'media', event: 'playing' });
    },
    loadeddata: markFrame,
    pause: () => dispatch({ type: 'media', event: 'pause' }),
    waiting: () => dispatch({ type: 'media', event: 'waiting' }),
    stalled: () => { if (video.readyState < 3) dispatch({ type: 'media', event: 'waiting' }); },
    ended: () => session?.capabilities.live
      ? dispatch({ type: 'fault', kind: 'network', error: faultError({ kind: 'network', reason: 'The live stream ended unexpectedly' }) })
      : dispatch({ type: 'media', event: 'ended' }),
    loadedmetadata: () => {
      const t = resumeAt;
      resumeAt = null;
      const d = video.duration;
      if (t !== null && t > 5 && (!Number.isFinite(d) || t < d - 10)) video.currentTime = t;
    },
  };
  const onVisible = () => { if (document.visibilityState === 'visible') void beat(); };
  const onPageHide = () => { void stop(); };
  for (const [ev, fn] of Object.entries(media)) video.addEventListener(ev, fn);
  document.addEventListener('visibilitychange', onVisible);
  window.addEventListener('pagehide', onPageHide);

  // ---- lease ----
  function release(sessionId: string) {
    const p = Promise.race([api('DELETE', sessionPath(sessionId), undefined, { keepalive: true }).catch(noop), sleep(RELEASE_TIMEOUT_MS)]);
    releasing = Promise.all([releasing, p]);
    return p;
  }
  async function beat() {
    const s = session;
    if (!s) return;
    const pos = video.currentTime;
    const state = m.status === 'playing' ? 'playing' : m.status === 'paused' ? 'paused' : 'buffering';
    try {
      await api('POST', `${sessionPath(s.sessionId)}/heartbeat`, { positionSec: Number.isFinite(pos) && pos >= 0 ? pos : undefined, state });
    } catch (err) {
      // 4xx = lease gone (SESSION_EXPIRED) or not ours: stop without retrying. Network blips wait for the next beat.
      if (session === s && err instanceof ApiRequestError && err.status >= 400 && err.status < 500 && err.status !== 429) {
        session = null;
        dispatch({ type: 'fail', error: toPlayerError(err) });
      }
    }
  }
  function stopHeartbeat() { clearInterval(hbTimer); hbTimer = undefined; }

  // ---- engine ----
  function destroyEngine() {
    attachSeq++;
    handle?.destroy();
    handle = null;
    video.removeAttribute('src');
    video.load();
  }
  function teardownMedia() {
    progress?.dispose();   // final flush reads currentTime before the media is torn down
    progress = null;
    clearTimeout(watchdog); clearTimeout(stableTimer); clearTimeout(parkTimer);
    destroyEngine();
    parked = false;
  }
  async function attach(my: number) {
    const s = session;
    if (!s) return;
    if (s.delivery === 'external') { dispatch({ type: 'external' }); return; }   // the UI offers external players instead
    const token = ++attachSeq;
    baseline = null;
    arm();   // covers slow engine loads, preflight and conversion probing too: never an endless spinner
    let r: Awaited<ReturnType<typeof attachEngine>>;
    const startSec = resumeAt !== null && resumeAt > 5 ? resumeAt : undefined;
    try {
      r = await attachEngine(video, s, {
        onFatal: (f) => { if (token === attachSeq && my === gen) fault(f); },
        onChange: () => {
          if (token !== attachSeq) return;
          tracks++; emit();
          // After the engine finishes its own handling: hls.js re-selects its default subtitle track right after
          // SUBTITLE_TRACKS_UPDATED, which would undo a choice made inside the event.
          queueMicrotask(() => { if (token === attachSeq && applyLanguages()) { tracks++; emit(); } });
        },
      }, { startSec, cacheKey: request?.itemId });
    } catch {
      if (my === gen) dispatch({ type: 'fail', error: { code: 'ENGINE_LOAD_FAILED', message: 'Tuned could not load its video player. Check your connection, then retry.', retryable: true } });
      return;
    }
    if (token !== attachSeq || my !== gen) {
      if ('destroy' in r) { r.destroy(); if (!handle) { video.removeAttribute('src'); video.load(); } }   // never disturb a newer engine
      return;
    }
    diagnostics = { ...diagnostics, decision: r.decision ?? null };
    if ('fault' in r) { fault(r.fault); return; }
    handle = r;
    applied = { audio: false, subtitle: false };
    applyLanguages();
    if (r.seek) resumeAt = null;   // the converter already started at startSec
    diagnostics = { ...diagnostics, engine: r.name };
    emit();
    startPlayback(my);
  }
  function fault(f: EngineFault) {
    diagnostics = { ...diagnostics, lastFault: `${f.kind}: ${f.reason}`.slice(0, 200) };
    if (f.httpStatus) diagnostics = { ...diagnostics, lastHttpStatus: f.httpStatus };
    dispatch({ type: 'fault', kind: f.kind, error: faultError(f) });
  }
  function startPlayback(my: number) {
    video.play().catch((e: unknown) => {
      // Autoplay blocked: show a paused player with a Play button rather than a spinner.
      if (my === gen && (e as { name?: string })?.name === 'NotAllowedError') { clearTimeout(watchdog); dispatch({ type: 'media', event: 'pause' }); }
    });
  }
  async function recover(kind: 'network' | 'media', attempt: number) {
    const my = gen;
    arm();
    const live = !!session?.capabilities.live;
    // Live media faults get a fresh engine at the live edge: hls.js recoverMediaError resumes at the old playhead, which
    // on a live stream can sit outside the current window (playhead jumps back, stalls, "Reconnecting" never ends).
    if (kind === 'media' && handle?.recoverMedia && !live) { handle.recoverMedia(attempt); startPlayback(my); return; }
    // A direct session's failure may be its expired token: reload with a fresh one instead of retrying the old URL.
    if (kind === 'network' && attempt === 1 && handle?.recoverNetwork && session?.delivery !== 'direct') { handle.recoverNetwork(); startPlayback(my); return; }
    if (!live && video.currentTime > 0) resumeAt = video.currentTime;   // reload at currentTime
    destroyEngine();
    // Back off before reopening upstream: providers release the previous connection with a lag, and a 1-connection
    // account would otherwise refuse the reconnect (PLAY04 bounded reconnects).
    await sleep(RECONNECT_BACKOFF_MS * attempt);
    if (my !== gen) return;
    if (session?.delivery === 'direct' && !(await renew(my))) return;
    await attach(my);
  }

  // ---- session ----
  const openLease = (req: PlayRequest) => api<PlaybackSession>('POST', '/api/playback', {
    sourceId: req.sourceId, mode: req.mode, programId: req.programId, positionSec: req.positionSec, tile, client: probeCapabilities(),
  });
  function adopt(s: PlaybackSession) {
    session = s;
    diagnostics = { ...diagnostics, delivery: s.delivery, mediaPath: s.playbackUrl };
    stopHeartbeat();
    hbTimer = setInterval(() => void beat(), Math.max(5, s.heartbeatIntervalSec || 15) * 1000);
  }
  /** ADR-0006: provider token URLs are short-lived bearer links. Reconnecting a direct session swaps its lease for a
   *  new one (and a fresh token URL) through POST /api/playback: old lease released first, never two at once. */
  async function renew(my: number): Promise<boolean> {
    const req = request, old = session;
    if (!req || !old) return false;
    stopHeartbeat();
    session = null;
    await release(old.sessionId);
    if (my !== gen) return false;
    try {
      const s = await openLease(req);
      if (my !== gen) { await release(s.sessionId); return false; }
      adopt(s);
      return true;
    } catch (err) {
      if (my === gen) dispatch({ type: 'fail', error: toPlayerError(err) });
      return false;
    }
  }

  // ---- languages (VOD03) ----
  const assetKey = () => request?.itemId ?? request?.channelId;
  /** When a track list first appears: this asset's last explicit choice, else the saved language, else the stream default. True when it switched a track. */
  function applyLanguages(): boolean {
    const h = handle;
    if (!h) return false;
    let changed = false;
    const mine = chosen.get(assetKey() ?? '');
    const prefs = opts.preferences?.() ?? null;
    const audio = h.audioTracks();
    if (!applied.audio && audio.length) {
      applied.audio = true;
      const id = trackFor(audio, mine?.audio ?? prefs?.audioLanguage);
      if (id !== undefined && id !== h.audioTrack()) { h.setAudioTrack(id); changed = true; }
    }
    const subs = h.subtitleTracks();
    if (!applied.subtitle && subs.length) {
      applied.subtitle = true;
      const want = mine && 'subtitle' in mine ? mine.subtitle : prefs ? prefs.subtitleLanguage : undefined;
      const id = want === null ? -1 : trackFor(subs, want);   // null = subtitles off
      if (id !== undefined && id !== h.subtitleTrack()) { h.setSubtitleTrack(id); changed = true; }
    }
    return changed;
  }
  /** Player menu: switch track, remember it for this asset, save its language as the default (debounced). */
  function chooseTrack(kind: 'audio' | 'subtitle', id: number) {
    const h = handle;
    if (!h) return;
    if (kind === 'audio') h.setAudioTrack(id); else h.setSubtitleTrack(id);
    const lang = id < 0 ? null : langKey((kind === 'audio' ? h.audioTracks() : h.subtitleTracks()).find((t) => t.id === id)?.lang);
    if (id >= 0 && !lang) return;   // an untagged track cannot become a language preference
    const key = assetKey();
    if (key) chosen.set(key, { ...chosen.get(key), ...(kind === 'audio' ? { audio: lang } : { subtitle: lang }) });
    prefPatch = { ...prefPatch, ...(kind === 'audio' ? { audioLanguage: lang } : { subtitleLanguage: lang }) };
    clearTimeout(prefTimer);
    prefTimer = setTimeout(flushPreferences, LANGUAGE_SAVE_MS);
  }
  function flushPreferences() {
    clearTimeout(prefTimer);
    const p = prefPatch;
    prefPatch = {};
    if (Object.keys(p).length) opts.onPreferences?.(p);
  }

  function park() {
    if (m.status !== 'paused' || !handle) return;
    destroyEngine();
    parked = true;
    emit();
  }

  // ---- public ----
  function closeCurrent() {
    teardownMedia();
    stopHeartbeat();
    if (session) void release(session.sessionId);
    session = null;
  }
  async function run(my: number, req: PlayRequest) {
    if (my !== gen) return;
    await releasing;                         // previous lease DELETE settles (≤3 s) before the new POST (PLAY05)
    if (my !== gen) return;
    let s: PlaybackSession;
    try {
      s = await openLease(req);
    } catch (err) {
      if (my === gen) dispatch({ type: 'fail', error: toPlayerError(err) });
      return;
    }
    if (my !== gen) { await release(s.sessionId); return; }
    diagnostics = noDiagnostics;
    adopt(s);
    resumeAt = req.mode === 'vod' ? s.resumePositionSec ?? req.positionSec ?? null : null;
    if (req.mode === 'vod' && req.itemId && opts.onProgress && s.delivery !== 'external') progress = trackProgress(video, { itemId: req.itemId, sessionId: s.sessionId }, opts.onProgress);
    dispatch({ type: 'resolved' });
    await attach(my);
  }

  function play(req: PlayRequest): Promise<void> {
    const my = ++gen;
    closeCurrent();
    request = req;
    diagnostics = noDiagnostics;
    playT0 = performance.now();
    firstFrame = null;
    dispatch({ type: 'play' });
    chain = chain.then(() => run(my, req));
    return chain;
  }

  /** Close: destroy the engine, clear timers, DELETE the lease (keepalive). Resolves once the DELETE settles. */
  function stop(): Promise<void> {
    gen++;
    closeCurrent();
    request = null;
    diagnostics = noDiagnostics;
    dispatch({ type: 'stop' });
    emit();
    return chain.then(() => releasing).then(noop);
  }

  function liveInfo(): LiveInfo | null {
    if (!session?.capabilities.live) return null;
    if (parked) return { window: null, delay: 0, expired: true };
    const w = handle?.seekWindow() ?? null;
    if (!w) return null;
    const t = video.currentTime;
    return { window: w, delay: Math.max(0, w.end - t - (baseline ?? 0)), expired: t < w.start - 1 };
  }
  /** Go live / Jump to live (PLAY04): never seeks to an offset outside the live window. */
  function goLive() {
    const my = gen;
    if (parked) { parked = false; emit(); void attach(my); return; }
    const w = handle?.seekWindow();
    if (w) video.currentTime = Math.max(w.start, w.end - (baseline ?? 0));
    startPlayback(my);
  }

  return {
    video,
    subscribe(l: () => void) { listeners.add(l); return () => { listeners.delete(l); }; },
    getSnapshot: () => snap,
    engine: () => handle,
    play,
    stop,
    retry: () => (request ? play(request) : Promise.resolve()),
    /** CONNECTION_LIMIT recovery: DELETE one of this device's sessions, then retry. Never touches other devices. */
    async stopSessionAndRetry(sessionId: string) { await release(sessionId); await (request ? play(request) : undefined); },
    togglePlay() {
      if (parked || liveInfo()?.expired) return goLive();
      if (video.paused || video.ended) startPlayback(gen); else video.pause();
    },
    goLive,
    liveInfo,
    /** Seek bar entry point: conversion restarts its pipeline, everything else seeks the element. */
    seekTo(sec: number) {
      if (handle?.seek) void handle.seek(sec).catch(() => {});
      else video.currentTime = sec;
    },
    firstFrameMs: () => firstFrame,
    chooseTrack,
    dispose() {
      flushPreferences();
      void stop();
      for (const [ev, fn] of Object.entries(media)) video.removeEventListener(ev, fn);
      document.removeEventListener('visibilitychange', onVisible);
      window.removeEventListener('pagehide', onPageHide);
    },
  };
}
